import React, { useState } from 'react';
import Price from './Price';
import s from '../CompareTable.scss';
import {
  TaxTypes,
  TypeCycle,
  TypeSelectedOffer,
} from '../../../types/general-types';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { Tooltip } from 'wix-style-react';
import PackageCycleModal from '../../PackageCycleModal/PackageCycleModal';
import { removeDrilldownParams } from '../../../services/window-utils';
import { biLogger } from '../../../globals';

export interface ProductHeaderProps extends InjectedExperimentsProps {
  productName: string;
  subtitle: string;
  isSticky: boolean;
  cycles: TypeCycle[];
  selectedOffer: TypeSelectedOffer;
  productId: string;
  productIndex: number;
  isSaleOn?: boolean;
  isEnabled?: boolean;
  currency?: string;
  shouldBeHighlighted?: boolean;
  priceAmount?: number;
  discountAmount?: number;
  taxType?: TaxTypes;
  isMostPopular?: boolean;
  isCurrentProduct?: boolean;
}

const ProductHeader = (props: ProductHeaderProps) => {
  const { t } = useTranslation();
  const {
    productName,
    subtitle,
    priceAmount,
    currency,
    taxType,
    shouldBeHighlighted,
    isSticky,
    isEnabled,
    isSaleOn,
    isMostPopular,
    cycles,
    productId,
    productIndex,
    isCurrentProduct,
  } = props;
  const [isCycleModalOpen, setIsCycleModalOpen] = useState(false);

  const closeModalHandler = () => {
    removeDrilldownParams();
    setIsCycleModalOpen(false);
  };

  const onClickSelectProduct = () => {
    const isChecked = productId === 'bcbc441f-47da-4735-b58f-e5386647bf5f';
    setIsCycleModalOpen(true);
    biLogger.ppSelected({
      buttonType: 'lower_table',
      package_name: productName,
      packageId: productId,
      isChecked,
    });
  };

  const selectedOffer = {
    ...props.selectedOffer,
    productId,
    productName,
  };

  const TaxMessage = () =>
    taxType === TaxTypes.VAT ? (
      <div className={s.tax}>{t('package.taxVAT')}</div>
    ) : taxType === TaxTypes.GST ? (
      <div className={s.tax}>{t('package.taxGST')}</div>
    ) : null;

  const renderInnerBadge = () => {
    if (isSaleOn && isSticky) {
      return (
        <div className={s.innerBadge} data-hook="compare-innerBadge-sale">
          {t('sale.badge.title')}
        </div>
      );
    }
    return (
      isMostPopular &&
      isEnabled &&
      isSticky && (
        <div
          className={s.innerBadge}
          data-hook="compare-innerBadge-mostPopular"
        >
          {t('package.mostPopular')}
        </div>
      )
    );
  };

  const ctaTooltipContent = () => {
    if (!priceAmount) {
      return t('compare.header.ctaTooltip.upgrade');
    }
    return `${
      isCurrentProduct ? t('compare.header.ctaTooltip.currentUpgrade') : ''
    }${t('compare.header.ctaTooltip.upgrade')}`;
  };

  return (
    <div
      className={classNames(s.headerContainer, {
        [s.mostPopularCell]: shouldBeHighlighted,
      })}
      data-hook="productHeader"
    >
      <div
        className={classNames(s.header, {
          [s.sticky]: isSticky,
        })}
      >
        {renderInnerBadge()}
        <div className={s.title} data-hook="productTitle">
          {productName}
        </div>
        <div className={s.subtitle} data-hook="productSubtitle">
          {subtitle}
        </div>
        <div className={s.divider} />
        <Price
          amount={priceAmount || 0}
          isSticky={isSticky}
          currency={currency}
          discountAmount={props.discountAmount}
        />
        <div className={s.selectContainer}>
          {!!priceAmount && (
            <div data-hook="compare-tax">
              <TaxMessage />
            </div>
          )}
          <Tooltip
            content={ctaTooltipContent()}
            disabled={isEnabled || false}
            placement="bottom"
            maxWidth={244}
            dataHook="compare-header-tooltip"
          >
            {priceAmount ? (
              <button
                disabled={!priceAmount || !isEnabled}
                className={s.select}
                onClick={onClickSelectProduct}
                data-hook="compare-header-select"
              >
                {t('package.select')}
              </button>
            ) : (
              <div
                className={s.preInstalled}
                data-hook="compare-header-preInstalled"
              >
                {t('package.free.preInstalled')}
              </div>
            )}
          </Tooltip>
        </div>
      </div>
      <PackageCycleModal
        isOpen={isCycleModalOpen}
        onClose={closeModalHandler}
        cycles={cycles}
        currency={currency || '&'}
        selectedOffer={selectedOffer}
        productIndex={productIndex}
        taxType={taxType}
      />
    </div>
  );
};

export default withExperiments(ProductHeader);
